import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Tooltip
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import EmptyState from "../EmptyState";
import { Section } from "../ui";
import { getTokens } from "../../styles/tokens";

import { Edit as EditIcon, OndemandVideo as OndemandVideoIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles(theme => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: 16
	},
	fullWidth: {
		width: '100%'
	},
    formGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 16,
		[theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' }
    },
    full: { gridColumn: '1 / -1' },
    field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    help: { fontSize: 12, color: t.textMuted },
    formActions: {
		gridColumn: '1 / -1',
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 8
    },
    deleteButton: { marginRight: 'auto', color: t.semantic.error },
    tablePaper: {
		width: '100%',
		overflow: 'hidden',
		boxShadow: t.shadow.card
    },
    tableContainer: {
		width: '100%',
		overflowX: 'auto',
		...theme.scrollbarStyles
    },
    table: { minWidth: 640 },
    titleCell: { fontWeight: 600, color: t.text },
    codeCell: {
		fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
		fontSize: 12.5,
		color: t.textSecondary,
		whiteSpace: 'nowrap'
    }
}});

export function HelpManagerForm (props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()

    const [record, setRecord] = useState(initialValue);

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async(data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(values) => (
                <Form className={classes.fullWidth}>
                    <div className={classes.formGrid}>
                        <div className={classes.field}>
                            <label className={classes.label} htmlFor="help-title">Título</label>
                            <Field
                                as={TextField}
                                id="help-title"
                                name="title"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </div>
                        <div className={classes.field}>
                            <label className={classes.label} htmlFor="help-video">
                                {i18n.t("helps.settings.codeVideo")}
                            </label>
                            <Field
                                as={TextField}
                                id="help-video"
                                name="video"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                            <span className={classes.help}>
                                Código do YouTube: o trecho depois de “v=” no link do vídeo.
                            </span>
                        </div>
                        <div className={`${classes.field} ${classes.full}`}>
                            <label className={classes.label} htmlFor="help-description">
                                {i18n.t("helps.settings.description")}
                            </label>
                            <Field
                                as={TextField}
                                id="help-description"
                                name="description"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </div>
                        <div className={classes.formActions}>
                            { record.id !== undefined ? (
                                <ButtonWithSpinner
                                    className={classes.deleteButton}
                                    loading={loading}
                                    onClick={() => onDelete(record)}
                                >
                                {i18n.t("helps.settings.delete")}
                                </ButtonWithSpinner>
                            ) : null}
                            <ButtonWithSpinner loading={loading} onClick={() => onCancel()}>
                            {i18n.t("helps.settings.clear")}
                            </ButtonWithSpinner>
                            <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                            {i18n.t("helps.settings.save")}
                            </ButtonWithSpinner>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    )
}

export function HelpsManagerGrid (props) {
    const { records, onSelect, loading, selectedId } = props
    const classes = useStyles()

    return (
        <Paper className={classes.tablePaper} variant="outlined">
            <div className={classes.tableContainer}>
            <Table className={classes.table} size="small" aria-label="Conteúdos de ajuda">
                <TableHead>
                <TableRow>
                    <TableCell align="left">Título</TableCell>
                    <TableCell align="left">{i18n.t("helps.settings.description")}</TableCell>
                    <TableCell align="left">Video</TableCell>
                    <TableCell align="right">Ações</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {records.map((row) => (
                    <TableRow key={row.id} selected={selectedId !== undefined && row.id === selectedId}>
                        <TableCell align="left" className={classes.titleCell}>{row.title || '-'}</TableCell>
                        <TableCell align="left">{row.description || '-'}</TableCell>
                        <TableCell align="left" className={classes.codeCell}>{row.video || '-'}</TableCell>
                        <TableCell align="right">
                            <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => onSelect(row)} aria-label="Editar">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                ))}
                {!loading && records.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={4} style={{ borderBottom: 'none' }}>
                            <EmptyState
                                icon={<OndemandVideoIcon />}
                                title="Nenhum conteúdo de ajuda cadastrado"
                                message="Preencha o formulário acima para publicar o primeiro vídeo."
                            />
                        </TableCell>
                    </TableRow>
                )}
                </TableBody>
            </Table>
            </div>
        </Paper>
    )
}

export default function HelpsManager () {
    const classes = useStyles()
    const { list, save, update, remove } = useHelps()
    
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        title: '',
        description: '',
        video: ''
    })

    useEffect(() => {
        async function fetchData () {
            await loadHelps()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadHelps = async () => {
        setLoading(true)
        try {
            const helpList = await list()
            setRecords(helpList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadHelps()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma helpo com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadHelps()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            title: '',
            description: '',
            video: ''
        })
    }

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            title: data.title || '',
            description: data.description || '',
            video: data.video || ''
        })
    }

    return (
        <div className={classes.root}>
            <Section title={record.id !== undefined ? "Editar conteúdo de ajuda" : "Novo conteúdo de ajuda"}>
                <HelpManagerForm 
                    initialValue={record} 
                    onDelete={handleOpenDeleteDialog} 
                    onSubmit={handleSubmit} 
                    onCancel={handleCancel} 
                    loading={loading}
                />
            </Section>
            <HelpsManagerGrid 
                records={records}
                onSelect={handleSelect}
                loading={loading}
                selectedId={record.id}
            />
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </div>
    )
}
